import React, { Fragment, useEffect, useState } from "react";
import { Card, Row, Col, Form} from "react-bootstrap";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";
import Resident from "../Models/Resident";
import InputMask from "react-input-mask"
import { ValidatorHelper } from "../../Shared/Utility/ValidatorHelper";

interface IProps {
    newResident: Resident
    runValidation: boolean;
    resetForm: boolean;
    isFormValid: boolean;
    updatePersonalInformation: (name: Resident) => void;
    updatePersonalInformationValidation: (isValid: boolean) => void;
}

export function PersonalInformationForm(props: IProps) {
    const formId: string = "nameForm";
    const ssn4FormId: string = "ssn4Id";
    const dobFormId: string = "DateOfBirth";
    const form = document.getElementById(formId) as HTMLFormElement;
    const firstNameElementId: string = "firstNameElement";
    const firstNameElement = document.getElementById(firstNameElementId) as HTMLInputElement;
    const lastNameElementId: string = "lastNameElement";
    const lastNameElement = document.getElementById(lastNameElementId) as HTMLInputElement;
    const middleNameElementId: string = "middleNameElement";
    const middleNameElement = document.getElementById(middleNameElementId) as HTMLInputElement;
    const suffixElementId: string = "suffixElement";
    const suffixNameElement = document.getElementById(suffixElementId) as HTMLInputElement;
    const hasNumber = /\d/; 
    const isNumber = /^\d+$/;

    const [inputMaskKey, setInputMaskKey] = useState<string>("id");
    const [dobInput, setDobInput] = useState<string>(props.newResident?.dateOfBirth);

    useEffect(() => {
        if (props.runValidation) validate();
    }, [props.runValidation]);

    useEffect(() => {
        if (props.resetForm) resetForm();
    }, [props.resetForm]);

    const updateLastFourOfSSN = (e: React.ChangeEvent<HTMLSelectElement>) => {
        let element = document.getElementById(ssn4FormId + inputMaskKey) as HTMLInputElement;
        ValidatorHelper.clearCustomValidation(element);
        let updated = deepCopy(props?.newResident);
        updated.ssn4 = e.target.value;
        updateResidentAndUpdatePersonalInformation(updated);
    }

    const updateFirstName = (e: React.ChangeEvent<HTMLSelectElement>) => {
        ValidatorHelper.clearCustomValidation(firstNameElement);
        var updated = deepCopy(props?.newResident);
        var updatedName = deepCopy(updated.name);
        updatedName.firstName = e.target.value;
        updated.name = updatedName;
        updateResidentAndUpdatePersonalInformation(updated);
    }

    const updateMiddleName = (e: React.ChangeEvent<HTMLSelectElement>) => {
        ValidatorHelper.clearCustomValidation(middleNameElement);
        var updated = deepCopy(props?.newResident);
        var updatedName = deepCopy(updated.name);
        updatedName.middleName = e.target.value;
        updated.name = updatedName;
        updateResidentAndUpdatePersonalInformation(updated);
    }

    const updateLastName = (e: React.ChangeEvent<HTMLSelectElement>) => {
        ValidatorHelper.clearCustomValidation(lastNameElement);
        var updated = deepCopy(props?.newResident);
        var updatedName = deepCopy(updated.name);
        updatedName.lastName = e.target.value;
        updated.name = updatedName;
        updateResidentAndUpdatePersonalInformation(updated);
    }

    const updateSuffix = (e: React.ChangeEvent<HTMLSelectElement>) => {
        ValidatorHelper.clearCustomValidation(suffixNameElement);
        var updated = deepCopy(props?.newResident);
        var updatedName = deepCopy(updated.name);
        updatedName.suffix = e.target.value;
        updated.name = updatedName;
        updateResidentAndUpdatePersonalInformation(updated);
    }

    const updateDateOfBirth = (e: React.ChangeEvent<HTMLSelectElement>) => {
        let element = document.getElementById(dobFormId + inputMaskKey) as HTMLInputElement;
        ValidatorHelper.clearCustomValidation(element);

        let updated = deepCopy(props?.newResident);
        updated.dateOfBirth = e.target.value;
        if (Date.parse(e.target.value)) {
            updated.dateOfBirth = getDateWithOffset(e.target.value).toJSON();
        }
        setDobInput(updated.dateOfBirth);
        updateResidentAndUpdatePersonalInformation(updated);
    }

    const validateNameInputs = () => {
        validateNameInput(props.newResident.name.firstName, firstNameElement);
        validateNameInput(props.newResident.name.lastName, lastNameElement);
        validateNameInput(props.newResident.name.middleName, middleNameElement);
        validateNameInput(props.newResident.name.suffix, suffixNameElement);
        validateSsn4Input(props.newResident.ssn4);
        validateDobInput(dobInput);
      }

    const validateNameInput = (name: string, element: HTMLInputElement) => {
        if (validateStringHasNumbers(name)) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Cannot enter numbers.");
        }
    }

    const validateSsn4Input = (ssn4: string) => {
        if (!isNumber.test(ssn4)) {
            let element = document.getElementById(ssn4FormId + inputMaskKey) as HTMLInputElement;
            ValidatorHelper.setCustomValidationWithFeedback(element, "Must enter exactly 4 numbers.");
        }
    }

    const validateDobInput = (dob: string) => {
        if (!Date.parse(dob)) {
            let element = document.getElementById(dobFormId + inputMaskKey) as HTMLInputElement;
            ValidatorHelper.setCustomValidationWithFeedback(element, "Date must be entered in MM/DD/YYYY format.");
        }
    }

    const validateStringHasNumbers = (name: string) => {
        return hasNumber.test(name);
    }

    const getDobStringFormatted = (dob: string) => {
        if (!dob) return "";

        if (Date.parse(dob)) {
            let newDate = new Date(dob);
            let year = newDate.getFullYear();
            let month = String(newDate.getMonth() + 1).padStart(2, '0');
            let day = String(newDate.getDate()).padStart(2, '0');
            return [month, day, year].join('/');
        }
        return dob;
    }

    const updateResidentAndUpdatePersonalInformation = (updated: Resident) => {
        props.updatePersonalInformation(updated);
    }

    const validate = () => {
        validateNameInputs();
        props.updatePersonalInformationValidation(form.checkValidity());
    }

    const resetForm = () => {
        let newId: string = inputMaskKey + 1
        setInputMaskKey(newId);
        form.reset();
    }

    return (
        <Fragment>
            <Card.Title className="mb-0 fw-bolder">
                <span className="text-primary">Personal</span>
            </Card.Title>
            <Row className="mt-3">
                <Col>
                    <Form noValidate id={formId} validated={props.isFormValid === false}>
                        <Row className="align-items-end mb-3">
                            <Col md={6}>
                                <Form.Group controlId={lastNameElementId}>
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Last Name<span className="text-primary ml-2">*</span></Form.Label>
                                    <Form.Control defaultValue={props.newResident?.name?.lastName} required type="input" tabIndex={1} onChange={(e: any) => {updateLastName(e)}}></Form.Control>
                                    <span className="invalid-feedback"></span>
                                </Form.Group>
                            </Col>
                            <Col md={{span: 3, offset: 1}}>
                                <Form.Group controlId={dobFormId + inputMaskKey}>
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">
                                        Date of Birth<span className="text-primary ml-2">*</span>
                                    </Form.Label>
                                    <InputMask mask="99/99/9999" 
                                        value={getDobStringFormatted(props?.newResident?.dateOfBirth)}
                                        onChange={(e: any) => updateDateOfBirth(e)}>
                                        {(inputProps: any) => {
                                            return <Form.Control {...inputProps} placeholder="MM/DD/YYYY" required type="input" tabIndex={5} />
                                        }}
                                    </InputMask>
                                    <span className="invalid-feedback"></span> 
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="align-items-end mb-3">
                            <Col md={6}>
                                <Form.Group controlId={firstNameElementId}>
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">First Name<span className="text-primary ml-2">*</span></Form.Label>
                                    <Form.Control defaultValue={props.newResident?.name?.firstName} required type="input" tabIndex={2} onChange={(e: any) => {updateFirstName(e)}}></Form.Control>
                                    <span className="invalid-feedback"></span>   
                                </Form.Group>
                                
                            </Col>
                            <Col md={{span: 3, offset: 1}}>
                                <Form.Group controlId={ssn4FormId + inputMaskKey}>
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Last 4 Digits of SSN<span className="text-primary ml-2">*</span></Form.Label>
                                    <InputMask mask="9999" 
                                        value={props?.newResident?.ssn4 ?? "" }
                                        onChange={(e: any) => updateLastFourOfSSN(e)}>
                                        {(inputProps: any) => {
                                            return <Form.Control  {...inputProps} placeholder="1234" required type="input" tabIndex={6} />
                                        }}
                                    </InputMask>
                                    <span className="invalid-feedback"></span>   
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="align-items-end mb-3">
                            <Col md={6}>
                                <Form.Group controlId={middleNameElementId}>
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Middle Name</Form.Label>
                                    <Form.Control defaultValue={props.newResident?.name?.middleName} type="input" tabIndex={3} onChange={(e: any) => {updateMiddleName(e)}}></Form.Control>
                                    <span className="invalid-feedback"></span>   
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="align-items-end mb-3">
                            <Col md={6}>
                                <Form.Group controlId={suffixElementId}>
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Suffix</Form.Label>
                                    <Form.Control defaultValue={props.newResident?.name?.suffix} type="input" tabIndex={4} onChange={(e: any) => {updateSuffix(e)}}></Form.Control>
                                    <span className="invalid-feedback"></span> 
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Fragment>
    );
}

export default PersonalInformationForm;
